import { useEffect, useState } from 'react';
import { getUserInfo } from 'utils/auth';

interface IRawArchiveData {
  hasAvailableArchives?: boolean;
  availableUndownloadedArchivesCount?: number;
  lastArchiveExpiresAt?: string | null;
}

interface IArchiveData {
  hasAvailableArchives?: boolean;
  availableUndownloadedArchivesCount?: number;
}

const isExpired = (lastArchiveExpiresAt?: string | null): boolean =>
  !!lastArchiveExpiresAt && new Date(lastArchiveExpiresAt).getTime() <= Date.now();

/**
 * Seeds from the persisted user info (kept in sync by updateUserData on every
 * auth/me refresh) instead of starting "unknown". Header/HeaderMobile remount
 * on every in-app navigation (each page renders its own MainLayout), so
 * starting from nothing here would hide the button on every page change even
 * while the archives are still valid.
 *
 * Also listens for the archiveCountUpdated event (dispatched by archive
 * polling) so a newly-ready archive shows the button immediately, without
 * waiting for a page change.
 *
 * The expiry check below is what actually prevents stale-cache flicker: if
 * the cached flag says available but the archive has since expired, it's
 * filtered out immediately on mount instead of showing until the next server
 * update corrects it.
 */
const useArchiveData = (): IArchiveData => {
  const [rawArchiveData, setRawArchiveData] = useState<IRawArchiveData>(() => {
    const { hasAvailableArchives, availableUndownloadedArchivesCount, lastArchiveExpiresAt } = getUserInfo();
    return { hasAvailableArchives, availableUndownloadedArchivesCount, lastArchiveExpiresAt };
  });

  useEffect(() => {
    const handleArchiveUpdate = (event: CustomEvent<IRawArchiveData>) => {
      setRawArchiveData({
        hasAvailableArchives: event.detail.hasAvailableArchives,
        availableUndownloadedArchivesCount: event.detail.availableUndownloadedArchivesCount,
        lastArchiveExpiresAt: event.detail.lastArchiveExpiresAt,
      });
    };

    window.addEventListener('archiveCountUpdated', handleArchiveUpdate as EventListener);
    return () => window.removeEventListener('archiveCountUpdated', handleArchiveUpdate as EventListener);
  }, []);

  return {
    hasAvailableArchives: rawArchiveData.hasAvailableArchives && !isExpired(rawArchiveData.lastArchiveExpiresAt),
    availableUndownloadedArchivesCount: rawArchiveData.availableUndownloadedArchivesCount,
  };
};

export default useArchiveData;
